import { useState, useEffect, useCallback } from 'react';
import { ApiError, NetworkError, TimeoutError } from 'shared-web';
import { toast } from 'shared-web/lib/toast';

// Generic API state
interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Hook for API calls with automatic loading/error states
export function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: unknown[] = [],
  options: {
    immediate?: boolean;
    onSuccess?: (data: T) => void;
    onError?: (error: string) => void;
    showErrorToast?: boolean;
    showSuccessToast?: boolean;
    successMessage?: string;
  } = {}
): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    immediate = true,
    onSuccess,
    onError,
    showErrorToast = true,
    showSuccessToast = false,
    successMessage = 'Operation completed successfully',
  } = options;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiCall();
      
      setData(result);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      if (showSuccessToast) {
        toast.success(successMessage);
      }
    } catch (err) {
      console.log(err)
      let errorMessage = 'An unexpected error occurred';
      
      if (err instanceof ApiError) {
        errorMessage = err.message;
      } else if (err instanceof NetworkError) {
        errorMessage = 'Network connection failed';
      } else if (err instanceof TimeoutError) {
        errorMessage = 'Request timed out';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      if (onError) {
        onError(errorMessage);
      }
      
      if (showErrorToast) {
        toast.apiError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [apiCall, onSuccess, onError, showErrorToast, showSuccessToast, successMessage, ...dependencies]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  }, dependencies);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

// Hook for mutations (POST, PUT, DELETE) with loading states
export function useMutation<TData, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: string, variables: TVariables) => void;
    showErrorToast?: boolean;
    showSuccessToast?: boolean;
    successMessage?: string | ((data: TData, variables: TVariables) => string);
  } = {}
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    onSuccess,
    onError,
    showErrorToast = true,
    showSuccessToast = true,
    successMessage = 'Operation completed successfully',
  } = options;

  const mutate = useCallback(async (variables: TVariables): Promise<TData | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await mutationFn(variables);
      
      if (onSuccess) {
        onSuccess(result, variables);
      }
      
      if (showSuccessToast) {
        const message = typeof successMessage === 'function' 
          ? successMessage(result, variables)
          : successMessage;
        toast.success(message);
      }
      
      return result;
    } catch (err) {
      console.log(err)
      let errorMessage = 'An unexpected error occurred';
      
      if (err instanceof ApiError) {
        errorMessage = err.message;
      } else if (err instanceof NetworkError) {
        errorMessage = 'Network connection failed';
      } else if (err instanceof TimeoutError) {
        errorMessage = 'Request timed out';
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      if (onError) {
        onError(errorMessage, variables);
      }
      
      if (showErrorToast) {
        toast.apiError(err);
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  }, [mutationFn, onSuccess, onError, showErrorToast, showSuccessToast, successMessage]);

  return {
    mutate,
    loading,
    error,
    reset: () => {
      setError(null);
      setLoading(false);
    },
  };
}

// Specific hooks for common patterns
export function usePaginatedApi<T>(
  apiCall: (params: { page: number; limit: number; [key: string]: any }) => Promise<{ data: T[]; pagination: any }>,
  initialParams: Record<string, any> = {},
  options: Parameters<typeof useApi>[2] = {}
) {
  const [params, setParams] = useState({ page: 1, limit: 12, ...initialParams });
  
  const result = useApi(
    () => apiCall(params),
    [params],
    options
  );

  const updateParams = useCallback((newParams: Partial<typeof params>) => {
    setParams(prev => ({ ...prev, ...newParams, page: 1 })); // Reset to page 1 when params change
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const resultData = result.data as { data: T[]; pagination: any } | null;

  return {
    ...result,
    params,
    updateParams,
    goToPage,
    pagination: resultData?.pagination,
    items: resultData?.data || [],
  };
}

// Hook for search with debouncing
export function useSearchApi<T>(
  searchFn: (query: string) => Promise<T>,
  debounceMs: number = 300,
  options: Parameters<typeof useApi>[2] = {}
) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  const result = useApi(
    () => searchFn(debouncedQuery),
    [debouncedQuery],
    { ...options, immediate: debouncedQuery.length > 0 }
  );

  return {
    ...result,
    query,
    setQuery,
    debouncedQuery,
  };
}